<script setup>
// One question from the assistant (S3): plain wording, a one-line why, two to
// six choices from the API and "Something else…" for free text. Everything the
// model wrote is rendered as text, never HTML (SE3). A single-choice question
// sends on the tap; a multi-select collects ticks and sends on Send.
import {computed, ref, watch} from 'vue';
import SetupGoalsTickList from '~/components/admin/setup/SetupGoalsTickList.vue';
import SetupSettingsCard from '~/components/admin/setup/SetupSettingsCard.vue';
import {PLAIN_QUESTION_LINE, filterChoiceRows, isChoiceGrid, isFreeTextRow, isPlainQuestion, isSettingsCard, questionChoiceRows} from '~/utils/setupAssistant';
import {isGoalsList} from '~/utils/setupGoals';

const props = defineProps({
  question: {type: Object, required: true},
  /** The card in the transcript after it was answered: no buttons, the answer shown by the user bubble that follows. */
  answered: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
});
const emit = defineEmits(['answer', 'something-else', 'confirm-settings']);

// X132: a server-built pick card's rows carry handles (sent on a click, never the label); other cards' rows are the model's options.
const rows = computed(() => questionChoiceRows(props.question));
const choices = computed(() => rows.value.map(r => r.label));
// X138 (R68 M2): many real choices on a multi-select card (every sport) = a compact tick grid with a filter box, never a cut list.
const grid = computed(() => isChoiceGrid(props.question));
const filter = ref('');
const shownRows = computed(() => (grid.value ? filterChoiceRows(rows.value, filter.value) : rows.value));
// X116: the goals card carries its rows (question.goals) and is drawn as a tickbox list with one Confirm, not as option pills.
const goalsList = computed(() => isGoalsList(props.question));
// X132: a settings card (every rule of a competition's form) is drawn as inline fields with one Confirm.
const settingsCard = computed(() => isSettingsCard(props.question));
// X254 (Grant: "surely it should just prompt me to type the information it needs"): a question with nothing to choose is a plain
// question: no pills (never a lone "Something else…"), one line saying where to type, and the composer focused and lit with the
// card's hint the moment the card opens (the page handles something-else).
const plain = computed(() => isPlainQuestion(props.question));
watch(() => [props.question?.id, plain.value, props.answered], ([, isPlain, answered]) => {
  if (isPlain && !answered && !props.disabled) emit('something-else', props.question);
}, {immediate: true});
// X105 (C8+): a card may open with options already ticked (the goals card pre-ticks what the club has or we inferred).
const preTicked = () => (props.question?.multiSelect ? (props.question.selected ?? []).filter(o => choices.value.includes(o)) : []);
const picked = ref(preTicked());
watch(() => props.question?.id, () => { picked.value = preTicked(); filter.value = ''; });

// X124 (R58 M-a): the goals list's on-screen ticks (keys), for a typed short yes; null on any other card.
const tickList = ref(null);
defineExpose({
  goalPicks: () => (goalsList.value ? [...(tickList.value?.picked ?? [])] : null),
  // X131: the on-screen Free / We'll charge answers, read with the ticks.
  goalCharges: () => (goalsList.value ? {...(tickList.value?.charges ?? {})} : null),
});

const handlesOf = (labels) => rows.value.filter(r => labels.includes(r.label) && r.handle).map(r => r.handle);
const choose = (row) => {
  if (props.answered || props.disabled) return;
  if (isFreeTextRow(row)) {
    emit('something-else', props.question);
    return;
  }
  const option = row.label;
  if (props.question.multiSelect) {
    picked.value = picked.value.includes(option) ? picked.value.filter(p => p !== option) : [...picked.value, option];
    return;
  }
  emit('answer', {question: props.question, picked: [option], handles: handlesOf([option])});
};
const sendPicked = () => {
  if (!picked.value.length) return;
  emit('answer', {question: props.question, picked: [...picked.value], handles: handlesOf(picked.value)});
};
</script>

<template>
  <div class="sqc" :class="{'sqc--answered': answered}">
    <div class="sqc-q">{{ question.text }}</div>
    <div v-if="question.why" class="sqc-why">{{ question.why }}</div>
    <setup-goals-tick-list
        v-if="goalsList"
        ref="tickList"
        :question="question"
        :answered="answered"
        :disabled="disabled"
        @answer="emit('answer', $event)"
    />
    <setup-settings-card
        v-if="settingsCard"
        :question="question"
        :answered="answered"
        :disabled="disabled"
        @confirm="emit('confirm-settings', $event)"
    />
    <v-text-field
        v-if="grid && !answered"
        v-model="filter"
        class="sqc-filter"
        density="compact"
        variant="outlined"
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        placeholder="Type to find one"
        :disabled="disabled"
        aria-label="Filter the choices"
    />
    <button
        v-if="plain && !answered"
        type="button"
        class="sqc-plain"
        data-testid="setup-plain-question"
        :disabled="disabled"
        @click="emit('something-else', question)"
    >
      <v-icon size="18" class="mr-2">mdi-keyboard-outline</v-icon>
      <span>{{ PLAIN_QUESTION_LINE }}</span>
    </button>
    <div v-if="!goalsList && !settingsCard && !plain" class="sqc-opts" :class="{'sqc-opts--grid': grid}">
      <button
          v-for="r in shownRows"
          :key="r.label"
          type="button"
          class="sqc-opt"
          :class="{'sqc-opt--else': r.else, 'sqc-opt--on': picked.includes(r.label), 'sqc-opt--lined': !!r.line, 'sqc-opt--tick': grid && !r.else}"
          :disabled="answered || disabled"
          :data-handle="r.handle || undefined"
          @click="choose(r)"
      >
        <v-icon v-if="question.multiSelect && !r.else" size="16" class="mr-1">{{ picked.includes(r.label) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
        <span class="sqc-opt-words">
          <span>{{ r.label }}</span>
          <span v-if="r.line" class="sqc-opt-line">{{ r.line }}</span>
        </span>
      </button>
    </div>
    <div v-if="!goalsList && !settingsCard && !plain && question.multiSelect && !answered" class="sqc-send">
      <v-btn size="small" color="primary" variant="flat" :disabled="disabled || !picked.length" @click="sendPicked">Send</v-btn>
      <span class="sqc-hint">{{ grid && picked.length ? `${picked.length} ticked. ` : '' }}Pick all that apply.</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sqc {
  border: 1px solid rgba(var(--v-theme-primary), 0.35);
  background: rgba(var(--v-theme-primary), 0.04);
  border-radius: 12px;
  padding: 12px 14px;

  &--answered {
    border-color: rgb(var(--v-theme-borderLight));
    background: transparent;
  }
}

.sqc-q {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.sqc-why {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 3px;
  white-space: pre-wrap;
  word-break: break-word;
}

.sqc-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.sqc-opt {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;

  &:hover:not(:disabled) { border-color: rgb(var(--v-theme-primary)); }
  &--on { border-color: rgb(var(--v-theme-primary)); background: rgb(var(--v-theme-brandTint)); }
  &--else { color: rgb(var(--v-theme-textSubtle)); font-weight: 500; border-style: dashed; }
  &--lined { border-radius: 12px; }
  &:disabled { cursor: default; opacity: 0.7; }
}

/* X254: a plain question's one line: where to type (brand tint, like a picked free-text row); a click brings the composer's focus back */
.sqc-plain {
  display: inline-flex;
  align-items: center;
  margin-top: 10px;
  border: 1px solid rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:disabled { cursor: default; opacity: 0.7; }
}

/* X132: a server-built choice may carry a line under its label ("Your club's own", a format's one-liner) */
.sqc-opt-words { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; min-width: 0; }
.sqc-opt-line { font-size: 11.5px; font-weight: 500; color: rgb(var(--v-theme-textSubtle)); line-height: 1.35; }

/* X138 (R68 M2): the sports card lists every sport: a filter box and a compact tick grid (two columns at 390, more as the card widens) */
.sqc-filter { margin-top: 10px; max-width: 320px; }

.sqc-opts--grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 2px 8px;
  margin-top: 8px;

  .sqc-opt--else { grid-column: 1 / -1; justify-self: start; margin-top: 6px; }
}

/* X143 (R70 L2): at phone width the card is about 250px wide (the transcript's bubble, not the viewport), where auto-fill at 140px gives ONE column; two fixed columns fit it */
@media (max-width: 599px) {
  .sqc-opts--grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.sqc-opt--tick {
  border-color: transparent;
  background: transparent;
  border-radius: 8px;
  padding: 5px 6px;
  justify-content: flex-start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover:not(:disabled) { border-color: transparent; background: rgba(var(--v-theme-primary), 0.06); }
  &.sqc-opt--on { border-color: transparent; background: rgb(var(--v-theme-brandTint)); }
}

.sqc-send { display: flex; align-items: center; gap: 10px; margin-top: 10px; }
.sqc-hint { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); }
</style>
